import type {
  DialogContentProps,
  DialogDescriptionProps,
  DialogTitleProps,
} from "@kobalte/core/dialog";
import { Dialog as DialogPrimitive } from "@kobalte/core/dialog";
import type { PolymorphicProps } from "@kobalte/core/polymorphic";
import type { ComponentProps, ParentProps, ValidComponent } from "solid-js";
import { Show, splitProps } from "solid-js";
import { cn } from "@/libs/cn";
import { withFocusReturn } from "./layer-focus";

export const Dialog = DialogPrimitive;
export const DialogTrigger = DialogPrimitive.Trigger;

export const DialogClose = DialogPrimitive.CloseButton;

export type dialogContentProps<T extends ValidComponent = "div"> = ParentProps<
  DialogContentProps<T> & {
    class?: string;
    /**
     * Hides the built-in close button. For dialogs the user must not dismiss —
     * e.g. a blocking progress dialog, where an X that cannot close anything
     * would contradict itself. Defaults to showing it.
     */
    hideCloseButton?: boolean;
  }
>;

export const DialogContent = <T extends ValidComponent = "div">(
  props: PolymorphicProps<T, dialogContentProps<T>>,
) => {
  const [local, rest] = splitProps(props as dialogContentProps, [
    "class",
    "children",
    "hideCloseButton",
    "onOpenAutoFocus",
    "onCloseAutoFocus",
  ]);
  const autoFocus = withFocusReturn(local);

  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay
        class={cn(
          "fixed inset-0 z-50 bg-background/80 data-expanded:animate-in data-closed:animate-out data-closed:fade-out-0 data-expanded:fade-in-0",
        )}
        {...rest}
      />
      {/*
        Below `sm` the dialog hangs from the top instead of sitting centered. A
        centered dialog re-centers whenever its height changes after opening
        (chips arriving from a query, a validation message), so everything above
        the change jumps by half of it — about 110 px on a phone. Anchored to the
        top, it only grows downward. The zoom follows the anchor (`origin-top`),
        so the top edge stays put while it opens and closes.

        `svh`, not `vh`: on iOS Safari `vh` is the viewport with the toolbars
        hidden, so with them showing, `5vh + 90vh` would put the bottom of a
        full-height dialog, and its buttons, under the bottom toolbar. `dvh`
        would follow the toolbars and move the dialog again. 5 + 90 + 5 leaves
        the same gap above and below a full-height dialog.
      */}
      <DialogPrimitive.Content
        class={cn(
          "fixed left-[50%] top-[5svh] z-50 grid w-full max-w-lg translate-x-[-50%] origin-top gap-4 border bg-card p-6 shadow-lg data-closed:duration-200 data-expanded:duration-200 data-expanded:animate-in data-closed:animate-out data-closed:fade-out-0 data-expanded:fade-in-0 data-closed:zoom-out-95 data-expanded:zoom-in-95 sm:top-[50%] sm:translate-y-[-50%] sm:origin-center sm:rounded-lg md:w-full max-h-[90svh] overflow-y-auto",
          local.class,
        )}
        onOpenAutoFocus={autoFocus.onOpenAutoFocus}
        onCloseAutoFocus={autoFocus.onCloseAutoFocus}
        {...rest}
      >
        {local.children}
        <Show when={!local.hideCloseButton}>
          <DialogPrimitive.CloseButton class="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-[opacity,box-shadow] hover:opacity-100 focus:outline-hidden focus:ring-[1.5px] focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              class="h-4 w-4"
            >
              <title>Close</title>
              <path
                fill="none"
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M18 6L6 18M6 6l12 12"
              />
            </svg>
          </DialogPrimitive.CloseButton>
        </Show>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
};

type dialogTitleProps<T extends ValidComponent = "h2"> = DialogTitleProps<T> & {
  class?: string;
};

export const DialogTitle = <T extends ValidComponent = "h2">(
  props: PolymorphicProps<T, dialogTitleProps<T>>,
) => {
  const [local, rest] = splitProps(props as dialogTitleProps, ["class"]);

  return (
    <DialogPrimitive.Title
      class={cn(
        "text-lg font-display font-semibold text-foreground",
        local.class,
      )}
      {...rest}
    />
  );
};

type dialogDescriptionProps<T extends ValidComponent = "p"> =
  DialogDescriptionProps<T> & {
    class?: string;
  };

export const DialogDescription = <T extends ValidComponent = "p">(
  props: PolymorphicProps<T, dialogDescriptionProps<T>>,
) => {
  const [local, rest] = splitProps(props as dialogDescriptionProps, ["class"]);

  return (
    <DialogPrimitive.Description
      class={cn("text-sm text-muted-foreground", local.class)}
      {...rest}
    />
  );
};

export const DialogHeader = (props: ComponentProps<"div">) => {
  const [local, rest] = splitProps(props, ["class"]);

  return (
    <div
      class={cn(
        "flex flex-col space-y-2 text-center sm:text-left",
        local.class,
      )}
      {...rest}
    />
  );
};

export const DialogFooter = (props: ComponentProps<"div">) => {
  const [local, rest] = splitProps(props, ["class"]);

  return (
    <div
      class={cn(
        "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
        local.class,
      )}
      {...rest}
    />
  );
};
